import { useState, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Menu, X, Zap } from "lucide-react";

// Lazy load to avoid circular import issues
const InlineSubscribeForm = lazy(() => 
  import("@/components/InlineSubscribeForm").then(module => ({ default: module.InlineSubscribeForm }))
);

// Navigation links matching Header.tsx for consistency
const navLinks = [
  { href: "/python-newsletter/", label: "Newsletter", preload: () => import("@/pages/PythonNewsletterPage") },
  { href: "/salary/", label: "Salary Guide", preload: () => import("@/pages/SalaryHub") },
  { href: "/hiring-trends/", label: "Market Trends", preload: () => import("@/pages/HiringTrends") },
  { href: "/interview-prep/", label: "Interview Prep", preload: () => import("@/pages/InterviewPrepHubPage") },
  { href: "/hiring-playbooks/", label: "Hiring Playbooks", preload: () => import("@/pages/HiringPlaybooksHub") },
  { href: "/cv-reviewer/", label: "CV Analyzer", preload: () => import("@/pages/CVReviewerPage") },
  { href: "/about/", label: "About", preload: () => import("@/pages/AboutPage") },
];

const preloadPodcast = () => import("@/pages/PodcastPage");
const preloadArchive = () => import("@/pages/NewslettersPage");

export const MobileMenu = () => {
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="lg:hidden"
          aria-label="Open navigation menu"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </SheetTrigger>
      <SheetContent 
        side="right" 
        className="w-[280px] sm:w-[320px] bg-background/95 backdrop-blur-xl border-primary/30"
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between mb-8">
            <span className="font-orbitron text-lg text-primary">Menu</span>
            <SheetClose asChild>
              <Button variant="ghost" size="icon" aria-label="Close menu">
                <X className="h-5 w-5" />
              </Button>
            </SheetClose>
          </div>
          
          <nav className="flex flex-col gap-2 flex-1">
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className="px-4 py-3 rounded-lg font-mono text-sm hover:bg-primary/10 hover:text-primary transition-colors border border-transparent hover:border-primary/30"
            >
              Home
            </Link>
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setOpen(false)}
                onMouseEnter={() => link.preload()}
                className="px-4 py-3 rounded-lg font-mono text-sm hover:bg-primary/10 hover:text-primary transition-colors border border-transparent hover:border-primary/30"
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/podcast/"
              onClick={() => setOpen(false)}
              onMouseEnter={() => preloadPodcast()}
              className="px-4 py-3 rounded-lg font-mono text-sm hover:bg-primary/10 hover:text-primary transition-colors border border-transparent hover:border-primary/30"
            >
              Podcast
            </Link>
            <Link
              to="/newsletters/"
              onClick={() => setOpen(false)}
              onMouseEnter={() => preloadArchive()}
              className="px-4 py-3 rounded-lg font-mono text-sm hover:bg-primary/10 hover:text-primary transition-colors border border-transparent hover:border-primary/30"
            >
              Archive
            </Link>
          </nav>
          
          <div className="pt-6 border-t border-border">
            <Suspense fallback={
              <Button variant="outline" size="sm" className="w-full gap-2" disabled>
                <Zap size={14} />
                Loading…
              </Button>
            }>
              <InlineSubscribeForm variant="minimal" />
            </Suspense>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
